// src/components/common/surveyCta.ts

import { fetchCurrentUser } from "../../api/auth";
import { messageToast } from "./toastMessage";
import { getCookie, setCookie } from "../../utils/cookies";
import { Toast } from "../../utils/bootstrap";
import { getEntries } from "../../services/entriesService";
import { hasCompletedSurveyRecently } from "../../services/surveyService";

const SURVEY_ID = "0199c3bd-f1c6-0000-c4ba-abbd15e8c923";
const SURVEY_URL = "/survey/ace-society-championship";
/** Cookie set when the toast is dismissed (X) or CTA clicked without completing. */
const SURVEY_CTA_DISMISSED_COOKIE = "surveyCtaDismissed";
const SURVEY_CTA_SESSION_KEY = "surveyCtaShownSession";
/**
 * Set after temp-claim success when a reload is about to happen. Showing the
 * CTA before reload would set surveyCtaShownSession and then wipe the toast.
 */
const SURVEY_CTA_PENDING_AFTER_CLAIM_KEY = "surveyCtaPendingAfterClaim";
const DISMISS_DAYS = 7;

export type SurveyCtaOptions = {
	/** Bypass route and temp-user guards (e.g. immediately after temp claim). */
	force?: boolean;
};

function hasPendingSurveyCtaAfterClaim(): boolean {
	try {
		return sessionStorage.getItem(SURVEY_CTA_PENDING_AFTER_CLAIM_KEY) === "true";
	} catch {
		return false;
	}
}

function clearPendingSurveyCtaAfterClaim(): void {
	try {
		sessionStorage.removeItem(SURVEY_CTA_PENDING_AFTER_CLAIM_KEY);
	} catch {
		/* ignore */
	}
}

async function userHasCompletedPaidEntry(): Promise<boolean> {
	try {
		const user = await fetchCurrentUser();
		if ((user.entry_count ?? 0) > 0) {
			return true;
		}
	} catch {
		/* fall through to entries list */
	}

	try {
		const entries = await getEntries();
		return entries.length > 0;
	} catch {
		return false;
	}
}

function isSurveyCtaSuppressed(): boolean {
	// Completed survey → hide CTA for at least 30 days
	if (hasCompletedSurveyRecently(SURVEY_ID)) {
		return true;
	}

	// Dismissed (or clicked through) → hide for 7 days
	// Also honour legacy surveyCtaHidden cookie from earlier builds
	if (
		getCookie(SURVEY_CTA_DISMISSED_COOKIE) ||
		getCookie("surveyCtaHidden")
	) {
		return true;
	}

	try {
		if (sessionStorage.getItem(SURVEY_CTA_SESSION_KEY) === "true") {
			return true;
		}
	} catch {
		/* ignore */
	}

	return false;
}

function renderSurveyCtaToast(): void {
	const msg = `
			<div class="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3">
				<span class="flex-grow-1"><strong>🙌 Want to WIN 50 free shots at the jackpot + 50 championship points?*</strong></span>
				<a href="${SURVEY_URL}" class="btn btn-success btn-sm flex-shrink-0">Take Short Survey</a>
			</div>`;

	setTimeout(() => {
		messageToast({ message: msg, background: "text-bg-light", delay: 0, bottomOffset: 0 });

		setTimeout(() => {
			const toasts = document.querySelectorAll<HTMLElement>(".ace-toast");
			const toastEl = toasts[toasts.length - 1];
			if (!toastEl) {
				console.warn("Survey CTA toast element not found");
				return;
			}

			// Only mark as shown once the toast actually landed in the DOM
			try {
				sessionStorage.setItem(SURVEY_CTA_SESSION_KEY, "true");
			} catch {
				/* ignore blocked storage */
			}

			const instance = Toast.getInstance(toastEl) || new Toast(toastEl);

			const dismissFor7Days = () => {
				try {
					setCookie(SURVEY_CTA_DISMISSED_COOKIE, "1", DISMISS_DAYS, "days");
				} catch {
					/* ignore */
				}
				try {
					instance.hide();
				} catch {
					/* ignore */
				}
			};

			const cta = toastEl.querySelector<HTMLAnchorElement>(
				`a[href="${SURVEY_URL}"]`,
			);
			const closeBtn = toastEl.querySelector<HTMLButtonElement>("button.btn-close");
			// Clicking through or closing both count as dismiss → 7 days
			cta?.addEventListener("click", dismissFor7Days);
			closeBtn?.addEventListener("click", dismissFor7Days);
		}, 100);
	}, 300);
}

/** Temp-claim email / save-recording sheet — never compete with it. */
function isTempClaimFlowOpen(): boolean {
	return !!document.getElementById("temp-claim-offcanvas");
}

/**
 * Shows the survey CTA as a toast for eligible users.
 * Default: logged-in, non-temp (or temp with a paid entry) users on home or dashboard.
 * Not shown while the temp-claim sheet is open, or for temps on entry pages.
 * After temp claim, a pending flag is consumed post-reload so the toast isn't
 * wiped by the claim-flow refresh.
 */
export async function showSurveyCtaIfEligible(
	options?: SurveyCtaOptions,
): Promise<void> {
	try {
		const path = window.location.pathname || "";
		const isLoggedIn = !!document.cookie.match(/(?:^|;\s*)token=/);
		if (!isLoggedIn) return;

		if (isSurveyCtaSuppressed()) {
			// Stale pending after dismiss/complete should not survive forever
			clearPendingSurveyCtaAfterClaim();
			return;
		}

		const pendingAfterClaim = hasPendingSurveyCtaAfterClaim();
		const force = options?.force === true || pendingAfterClaim;

		if (!force) {
			// Claim / save-recording flow owns the screen until it finishes
			if (isTempClaimFlowOpen()) return;

			const isTemporary = getCookie("is_temporary") === "true";
			const isEntryPage = /(?:^|\/)dashboard\/entry(?:\/|$)/.test(path);
			// Entry page is where temp claim runs; don't overlay the CTA there
			if (isTemporary && isEntryPage) return;

			if (isTemporary && !(await userHasCompletedPaidEntry())) {
				return;
			}

			const isHome = path === "/" || path === "";
			const isDashboard = /(?:^|\/)dashboard(?:\/|$)/.test(path);
			if (!isHome && !isDashboard) return;
			if (/(?:^|\/)survey(?:\/|$)/.test(path)) return;
		}

		if (pendingAfterClaim) {
			clearPendingSurveyCtaAfterClaim();
		}

		renderSurveyCtaToast();
	} catch {
		/* ignore */
	}
}

/**
 * Queue the survey CTA for after the post-claim reload.
 * Do not show immediately — the claim flow reloads ~3.5s later and would
 * otherwise mark surveyCtaShownSession while destroying the toast.
 */
export function showSurveyCtaAfterTempClaim(): void {
	try {
		sessionStorage.setItem(SURVEY_CTA_PENDING_AFTER_CLAIM_KEY, "true");
	} catch {
		/* ignore blocked storage */
	}
}
